import React, { useState, useMemo } from 'react';
import { PlusIcon, SearchIcon, PencilIcon, BarChart3Icon, MailIcon, MessageSquareIcon, PhoneIcon, BellIcon, GlobeIcon, Share2Icon, UsersIcon, TargetIcon, MapPinIcon, FilterIcon, InboxIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getCategoryLabel } from '../data/communications';
import type { ChannelKey, Notice, NoticeStatus } from '../types/notice';
const CHANNEL_ICONS: Record<ChannelKey, typeof MailIcon> = {
  email: MailIcon,
  sms: MessageSquareIcon,
  voice: PhoneIcon,
  push: BellIcon,
  website: GlobeIcon,
  social: Share2Icon
};
const AUDIENCE_ICONS = {
  org: UsersIcon,
  sector: TargetIcon,
  location: MapPinIcon,
  filter: FilterIcon
};
interface NoticeListProps {
  notices: Notice[];
  onCreate: () => void;
  onEdit: (notice: Notice) => void;
  onViewStats: (notice: Notice) => void;
}
export function NoticeList({
  notices,
  onCreate,
  onEdit,
  onViewStats
}: NoticeListProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [status, setStatus] = useState<NoticeStatus | 'all'>('all');
  const [query, setQuery] = useState('');
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const statusMeta: Record<NoticeStatus, {
    label: string;
    className: string;
    dot: string;
  }> = {
    sent: {
      label: t('Envoyé', 'Sent'),
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      dot: 'bg-emerald-500'
    },
    scheduled: {
      label: t('Programmé', 'Scheduled'),
      className: 'bg-amber-50 text-amber-700 border-amber-200',
      dot: 'bg-amber-500'
    },
    draft: {
      label: t('Brouillon', 'Draft'),
      className: 'bg-slate-100 text-slate-600 border-slate-200',
      dot: 'bg-slate-400'
    }
  };
  const audienceLabel = (notice: Notice) => {
    if (notice.audience.detail) return notice.audience.detail[lang];
    switch (notice.audience.kind) {
      case 'org':
        return t("Toute l'organisation", 'Entire organization');
      case 'sector':
        return t('Secteurs ciblés', 'Targeted districts');
      case 'location':
        return t('Autour d’un lieu', 'Around a location');
      default:
        return t('Audience filtrée', 'Filtered audience');
    }
  };
  const filters: {
    id: NoticeStatus | 'all';
    label: string;
  }[] = [{
    id: 'all',
    label: t('Tous', 'All')
  }, {
    id: 'sent',
    label: t('Envoyés', 'Sent')
  }, {
    id: 'scheduled',
    label: t('Programmés', 'Scheduled')
  }, {
    id: 'draft',
    label: t('Brouillons', 'Drafts')
  }];
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return notices.filter(n => status === 'all' ? true : n.status === status).filter(n => q ? n.title.toLowerCase().includes(q) || getCategoryLabel(n.category)[lang].toLowerCase().includes(q) : true).sort((a, b) => +new Date(b.date) - +new Date(a.date));
  }, [notices, status, query, lang]);
  const countFor = (id: NoticeStatus | 'all') => id === 'all' ? notices.length : notices.filter(n => n.status === id).length;
  return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-4123" data-mp="2adf5jswwbft4">
      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between" data-id="element-4124" data-mp="2f4x4kbfcz6bg">
        <div className="flex flex-wrap items-center gap-2" data-id="element-4125" data-mp="2k4t7s8g8etpy">
          {filters.map(f => {
          const active = status === f.id;
          return <button key={f.id} onClick={() => setStatus(f.id)} className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors border ${active ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-id="element-4126" data-mp="1mim2ewc3sxcj">
                {f.label}
                <span className={`ml-2 text-xs ${active ? 'text-slate-300' : 'text-slate-400'}`} data-id="element-4127" data-mp="3csfn2dytnmsn">
                  {countFor(f.id)}
                </span>
              </button>;
        })}
        </div>

        <div className="flex items-center gap-3" data-id="element-4128" data-mp="36ei3jjsw46er">
          <div className="relative flex-1 lg:w-72" data-id="element-4129" data-mp="3kczqvgevvgw5">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-4130" data-mp="3kadhrcccgtbt" />
            <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={t('Rechercher un avis…', 'Search notices…')} aria-label={t('Rechercher un avis', 'Search notices')} className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-4131" data-mp="3td4x3n8cc98q" />
          </div>
          <button onClick={onCreate} className="shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm" data-id="element-4132" data-mp="gvznutzu9wf9">
            <PlusIcon className="w-4 h-4" data-id="element-4133" data-mp="zjry53xa7fg0" />
            {t('Créer un avis', 'Create a notice')}
          </button>
        </div>
      </div>

      {/* List */}
      {visible.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 py-16 px-6 text-center" data-id="element-4134" data-mp="qjdba04o5sae">
          <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3" data-id="element-4135" data-mp="buh6cbxwsve0">
            <InboxIcon className="w-5 h-5 text-slate-400" data-id="element-4136" data-mp="gkxlq1lair30" />
          </div>
          <p className="text-sm font-medium text-slate-600" data-id="element-4137" data-mp="gn0kba0idsjc">
            {t('Aucun avis ne correspond à ces critères.', 'No notice matches these criteria.')}
          </p>
          <p className="text-xs text-slate-400 mt-1" data-id="element-4138" data-mp="7tdqhpfv2zcs">
            {t('Ajustez la recherche ou créez un nouvel avis.', 'Adjust your search or create a new notice.')}
          </p>
        </div> : <ul className="space-y-2" data-id="element-4139" data-mp="3av77b2rx4tg3">
          {visible.map(notice => {
        const meta = statusMeta[notice.status];
        const AudienceIcon = AUDIENCE_ICONS[notice.audience.kind];
        return <li key={notice.id} onDoubleClick={() => onEdit(notice)} className="group bg-white rounded-xl border border-slate-200 px-5 py-4 hover:border-[#4F46E5]/40 hover:shadow-sm transition-all" data-id="element-4140" data-mp="314uphibhnedn">
                <div className="flex items-center gap-4" data-id="element-4141" data-mp="3tryydydpccup">
                  {/* Status + category, stacked so neither crowds the title. */}
                  <div className="hidden sm:flex flex-col gap-1 w-32 shrink-0" data-id="element-4142" data-mp="3jvccd69n3fr3">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border w-fit ${meta.className}`} data-id="element-4143" data-mp="3exkc9oyucnbt">
                      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} data-id="element-4144" data-mp="23i5fal6mtvgp" />
                      {meta.label}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 text-xs font-medium border border-slate-200 w-fit truncate max-w-full" data-id="element-4145" data-mp="j163t3hpdxbe">
                      {getCategoryLabel(notice.category)[lang]}
                    </span>
                  </div>

                  <div className="min-w-0 flex-1" data-id="element-4146" data-mp="xp0s87oohfio">
                    <h3 className="text-base font-semibold text-[#0F172A] truncate" data-id="element-4147" data-mp="xqtznp7483rq">
                      {notice.title}
                    </h3>

                    <div className="flex items-center gap-3 flex-wrap mt-1 text-xs text-slate-500" data-id="element-4148" data-mp="16onuevp8epcs">
                      <span data-id="element-4149" data-mp="1bpk09et58lku">
                        {notice.status === 'scheduled' ? t('Envoi prévu le ', 'Sending on ') : notice.status === 'draft' ? t('Modifié le ', 'Edited on ') : t('Envoyé le ', 'Sent on ')}
                        <span className="font-medium text-slate-700" data-id="element-4150" data-mp="3ifi3fxqsz32p">
                          {new Date(notice.date).toLocaleDateString(locale, {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                        </span>
                      </span>
                      <span aria-hidden="true" className="text-slate-300" data-id="element-4151" data-mp="1sxky367i4mg2">
                        ·
                      </span>
                      <span className="inline-flex items-center gap-1.5" data-id="element-4152" data-mp="158s77o5ql4sb">
                        <AudienceIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-4153" data-mp="10a8mts6sk122" />
                        <span className="font-medium text-slate-700" data-id="element-4154" data-mp="1f6zch9qnxyeh">
                          {notice.audience.count.toLocaleString(locale)}
                        </span>
                        <span className="hidden md:inline" data-id="element-4155" data-mp="1a9pozzmu3wta">
                          {audienceLabel(notice)}
                        </span>
                      </span>
                      <span aria-hidden="true" className="text-slate-300" data-id="element-4156" data-mp="2lm081ebs0ekj">
                        ·
                      </span>
                      <span className="inline-flex items-center gap-1.5" data-id="element-4157" data-mp="2t8dsmow6099r">
                        {notice.channels.map(c => {
                    const Icon = CHANNEL_ICONS[c];
                    return <Icon key={c} className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" data-id="element-4158" data-mp="2o6epng4c7edc" />;
                  })}
                        <span className="sr-only" data-id="element-4159" data-mp="3gxg9vvmw003q">
                          {notice.channels.length} {t('canaux', 'channels')}
                        </span>
                      </span>
                    </div>
                  </div>

                  {notice.status === 'sent' && notice.stats && <div className="hidden xl:flex items-center gap-5 shrink-0 text-right" data-id="element-4160" data-mp="3gpd59r3w4nt">
                      <div data-id="element-4161" data-mp="3ik16wxoccw4">
                        <div className="text-sm font-bold text-[#0F172A]" data-id="element-4162" data-mp="crwxkgyif4pw">
                          {notice.stats.openRate} %
                        </div>
                        <div className="text-[11px] text-slate-400" data-id="element-4163" data-mp="hpgks6d6qrtz">
                          {t('Ouverture', 'Open rate')}
                        </div>
                      </div>
                      <div data-id="element-4164" data-mp="7rseqka4pxwi">
                        <div className="text-sm font-bold text-[#0F172A]" data-id="element-4165" data-mp="7v6j1y2csh6l">
                          {notice.stats.clickRate} %
                        </div>
                        <div className="text-[11px] text-slate-400" data-id="element-4166" data-mp="3uyupxmn7zkxk">
                          {t('Clics', 'Clicks')}
                        </div>
                      </div>
                    </div>}

                  {/* Revealed on hover — kept reachable by keyboard at all times. */}
                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity" data-id="element-4167" data-mp="3790glf240ei2">
                    <button onClick={() => onEdit(notice)} aria-label={t(`Modifier « ${notice.title} »`, `Edit “${notice.title}”`)} title={t('Modifier', 'Edit')} className="p-2 rounded-lg text-slate-500 hover:text-[#4F46E5] hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] transition-colors" data-id="element-4168" data-mp="2sli805lfezqd">
                      <PencilIcon className="w-4 h-4" data-id="element-4169" data-mp="36lsz99qnegc2" />
                    </button>
                    <button onClick={() => onViewStats(notice)} aria-label={t(`Statistiques de « ${notice.title} »`, `Statistics for “${notice.title}”`)} title={t('Statistiques', 'Statistics')} className="p-2 rounded-lg text-slate-500 hover:text-[#4F46E5] hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] transition-colors" data-id="element-4170" data-mp="36jq9mqteqp7v">
                      <BarChart3Icon className="w-4 h-4" data-id="element-4171" data-mp="2qzpxt4y7ds6h" />
                    </button>
                  </div>
                </div>
              </li>;
      })}
        </ul>}
    </div>;
}